import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Globe, DollarSign } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a US invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A standard US invoice should include: your business name and address; your client name and address; a unique invoice number; the invoice date and payment due date; a description of the goods or services provided; the amount charged for each line item; the total amount due; and your payment details (bank account, ACH, wire transfer, PayPal, or other method). If you collect state sales tax, include the applicable tax rate and amount separately.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do freelancers in the US need to charge sales tax?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Most US freelancers providing services (design, development, consulting, writing) are not required to collect sales tax, as services are generally not taxable in most states. However, rules vary by state — a few states do tax certain services. Sales tax on physical products or software may also apply. Consult a tax professional for your specific state and service type.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is there a free invoice generator for US businesses?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is a free invoice generator for US freelancers, contractors, and businesses. Create professional PDF invoices in USD. No sign-up required for up to 5 invoices per month. Supports hourly billing, project fees, and itemized invoices.',
      },
    },
    {
      '@type': 'Question',
      name: 'What payment terms should I use on a US invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Common US invoice payment terms are Net 15 (due in 15 days), Net 30 (due in 30 days), or Due on Receipt. Net 30 is the most widely accepted standard for B2B invoices. For new clients or small projects, Due on Receipt or Net 15 reduces late payment risk. Always include a specific due date alongside the payment terms.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I add my EIN or SSN to a US invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can add your EIN (Employer Identification Number) to invoices if you have one, though it is not required on most invoices. Some clients, particularly larger corporations, may request your EIN for their W-9 records before paying contractors. Your SSN should never appear on an invoice — use an EIN instead if a tax ID is requested.',
      },
    },
  ],
};

export default function InvoiceGeneratorUSPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator for US Freelancers & Businesses: Free PDF | Invilic"
        description="Free invoice generator for US freelancers, contractors, and small businesses. Create professional PDF invoices in USD. No sign-up needed. Net 30, hourly billing, and more."
        canonical="https://www.invilic.com/invoice-generator-us"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇺🇸 United States
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator for US Businesses
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for US freelancers, contractors, and small businesses. Create professional USD invoices with Net 30, hourly billing, or project fees. Download a clean PDF in seconds — no account needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/create/freelancer"
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200"
            >
              Create Invoice Free <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/create/international"
              className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 text-white font-semibold px-6 py-4 rounded-xl transition-all duration-200"
            >
              Bill International Clients <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        {/* Which invoice */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Which Invoice Do You Need?
          </h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {[
              {
                icon: FileText,
                title: 'Standard Invoice',
                subtitle: 'For US freelancers & contractors',
                desc: 'Bill clients in USD for services, consulting, or project work. No sales tax required for most service-based businesses.',
                features: ['USD billing', 'Hourly or project rate', 'Net 15 / Net 30 terms', 'Logo and branding', 'ACH / wire transfer details'],
                to: '/create/freelancer',
                color: 'blue',
                btnClass: 'bg-blue-600 hover:bg-blue-700',
              },
              {
                icon: Globe,
                title: 'International Invoice',
                subtitle: 'For US businesses billing overseas',
                desc: 'Bill clients in the UK, Europe, India, or anywhere else in USD, EUR, GBP, or other currencies.',
                features: ['Multi-currency (USD, EUR, GBP)', 'SWIFT / IBAN fields', 'Wire transfer details', 'Export-friendly format'],
                to: '/create/international',
                color: 'indigo',
                btnClass: 'bg-indigo-600 hover:bg-indigo-700',
              },
            ].map(({ icon: Icon, title, subtitle, desc, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-3`}>{subtitle}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{desc}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={to}
                  className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}
                >
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* What's included */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's on Your US Invoice
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Your business name, address & contact',
              'Client name and billing address',
              'Unique invoice number (auto-incremented)',
              'Invoice date and payment due date',
              'Service description per line item',
              'Hours or quantity with unit rate',
              'Subtotal, optional discount and tax',
              'Total amount in USD',
              'Net 15 / Net 30 payment terms',
              'ACH, wire transfer, or payment link details',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-blue-100 dark:border-blue-800/30 bg-blue-50 dark:bg-blue-900/10">
                <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Sales tax note */}
        <section className="mb-16 p-6 rounded-2xl border border-amber-100 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/20">
          <div className="flex items-start gap-3">
            <DollarSign className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-bold text-gray-900 dark:text-white mb-2">A Note on US Sales Tax</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                The US has no federal VAT or GST. Sales tax is state-level and varies significantly. Most service-based freelancers (design, development, consulting, writing) are not required to collect sales tax. If you do need to add sales tax, use the tax field in Invilic and enter your applicable state rate. When in doubt, consult a US tax professional.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            US Invoice Generator: Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your US Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF invoice in under 2 minutes. No account required.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200 text-lg"
          >
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
